import { useCallback, useEffect, useMemo, useState } from 'react';
import { useReactiveVar } from '@apollo/client';
import { shoppingCartOffersVar } from '../../../apollo/reactive-variables/shopping-cart';
import currencyVar from '../../../apollo/reactive-variables/money';
import useConstrainedGridSelection from '../../../hooks/useConstrainedGridSelection';
import useIsMobile from '../../../hooks/useIsMobile';
import { DISABLED_ROW_CLASS_NAME } from '../../../theme/constants/data-grid';

const useAvailableOffersStateBase = ({ getRowConstraintValue } = {}) => {
  const isMobile = useIsMobile('tablet');
  const shoppingCartOffers = useReactiveVar(shoppingCartOffersVar);
  const offerIds = useMemo(
    () => shoppingCartOffers.map((offer) => offer.invoiceId),
    [shoppingCartOffers],
  );
  const currency = useReactiveVar(currencyVar);

  const [showConfirmationSummary, setShowConfirmationSummary] = useState(false);
  const [showCessionStep, setShowCessionStep] = useState(false);

  const {
    lockedConstraint,
    syncGridRows,
    onSelectionChange,
    isRowSelectable: isConstraintRowSelectable,
    getRowClassName,
  } = useConstrainedGridSelection({
    disabledRowClassName: DISABLED_ROW_CLASS_NAME,
    getRowConstraintValue,
  });

  useEffect(() => {
    if (offerIds.length === 0) {
      setShowConfirmationSummary(false);
    }
  }, [offerIds]);

  const handleSelectionModelChange = useCallback((ids) => {
    const filteredIds = onSelectionChange(ids);
    const newSelectedInvoices = filteredIds.map((id) => {
      const existingOffer = shoppingCartOffers.find(
        ({ invoiceId }) => String(invoiceId) === String(id),
      );
      return existingOffer || { invoiceId: id, dateToPay: null };
    });
    shoppingCartOffersVar(newSelectedInvoices);
  }, [onSelectionChange, shoppingCartOffers]);

  const onCompletedSetRows = useCallback((data) => {
    const rows = data.invoices.edges.map((edge) => edge.node);
    syncGridRows(rows);
    return rows;
  }, [syncGridRows]);

  const isRowSelectable = useCallback((params) => {
    if (params.row.isForOrdering) return false;
    return isConstraintRowSelectable(params);
  }, [isConstraintRowSelectable]);

  return {
    isMobile,
    shoppingCartOffers,
    offerIds,
    currency,
    showConfirmationSummary,
    setShowConfirmationSummary,
    showCessionStep,
    setShowCessionStep,
    lockedConstraint,
    getRowClassName,
    isRowSelectable,
    handleSelectionModelChange,
    onCompletedSetRows,
  };
};

export default useAvailableOffersStateBase;
